import "./index.css"

import Icon from "@utilities/Icon"
import { useId } from "@utilities/useId"
import classNames from "classnames"
import React, { forwardRef, type SelectHTMLAttributes, useRef } from "react"

import { SelectNative } from "./SelectNative"
import { SelectPopover } from "./SelectPopover"

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type SelectSize = "lg" | "md"

/**
 * String-labelled option variant. Valid in both native (`complex={false}`)
 * and complex (`complex={true}`) modes. The `label` doubles as the type-ahead
 * search target.
 */
export interface SelectOptionWithTextLabel {
  /** Arbitrary `data-*` attributes are forwarded to the rendered option. */
  [key: `data-${string}`]: boolean | number | string | undefined
  /** Whether this option is non-interactive. */
  disabled?: boolean
  divider?: never
  /** Displayed as the option content and used for type-ahead matching. */
  label: string
  textValue?: never
  /** Unique identifier for the option. */
  value: string
}

/**
 * Rich-content option variant. The popover (`complex={true}`) renders the
 * `label` node directly; the native `<select>` (`complex={false}`) falls back
 * to `textValue` for the visible text. Requires a plain-text `textValue` so
 * type-ahead matching, screen reader announcements, and the native fallback
 * all have something stable to use.
 */
export interface SelectOptionWithNodeLabel {
  /** Arbitrary `data-*` attributes are forwarded to the rendered option. */
  [key: `data-${string}`]: boolean | number | string | undefined
  /** Whether this option is non-interactive. */
  disabled?: boolean
  divider?: never
  /**
   * Option content. Rendered as JSX in complex mode; in native mode, the
   * visible text comes from `textValue` instead.
   */
  label: React.ReactNode
  /**
   * Plain-text fallback for type-ahead matching, screen reader
   * announcements, and the native `<select>`'s visible option text when
   * `label` is a React node.
   */
  textValue: string
  /** Unique identifier for the option. */
  value: string
}

/**
 * Visual-separator option variant. Renders a divider line between options
 * (or between groups of options). Carries no value of its own.
 */
export interface SelectOptionDivider {
  disabled?: never
  /** Renders a visual separator between options. */
  divider: true
  label?: never
  textValue?: never
  value?: never
}

export type SelectOption =
  | SelectOptionDivider
  | SelectOptionWithNodeLabel
  | SelectOptionWithTextLabel

/**
 * A group entry in `Select`'s `options` array. Renders a heading above its
 * `options` and nests them visually beneath it.
 */
export interface SelectOptionsGroup {
  /**
   * Whether all options in this group are non-interactive. Disables every
   * nested option regardless of its own `disabled` flag.
   */
  disabled?: boolean
  /** Visible heading displayed above the group's options. */
  label: string
  /** The selectable options nested under this group. */
  options: SelectOption[]
}

export type SelectItem = SelectOption | SelectOptionsGroup

// ---------------------------------------------------------------------------
// Select component props
// ---------------------------------------------------------------------------

interface SelectBaseProps {
  /**
   * When true, renders as a popover listbox instead of a native `<select>`.
   * Enables rich content in options (icons, descriptions).
   */
  complex?: boolean
  /**
   * Helper text displayed below the select. Styled as error text when
   * `invalid`. Defaults to `"Please select an option"` when `invalid` is true
   * and no description is provided.
   */
  description?: string
  /**
   * Whether the select is in an invalid state. Shows the default description
   * if none is provided.
   */
  invalid?: boolean
  /** A flat or mixed array of options and option groups. */
  options: SelectItem[]
  /** Placeholder text shown when no option is selected. Defaults to `"Select an option"`. */
  placeholder?: string
  /** The size of the select. */
  size?: SelectSize
}

interface SelectWithVisibleLabel extends SelectBaseProps {
  /** If true, the label is visually hidden and set as aria-label. */
  hideLabel?: false
  /**
   * The label text for the select. Required unless `aria-label` or
   * `aria-labelledby` is provided.
   */
  label: string
}

interface SelectWithHiddenLabel extends SelectBaseProps {
  /** If true, the label is visually hidden and set as aria-label. */
  hideLabel: true
  /** The label text for the select. Must be a string when hidden. */
  label: string
}

interface SelectWithAriaLabel extends SelectBaseProps {
  /** Accessible label for the select. Use when no visible label exists. */
  "aria-label": string
  hideLabel?: never
  label?: never
}

interface SelectWithAriaLabelledBy extends SelectBaseProps {
  /**
   * ID of an external label element. Use instead of `label` when a visible
   * label exists elsewhere on the page.
   */
  "aria-labelledby": string
  hideLabel?: never
  label?: never
}

export type SelectProps =
  | SelectWithAriaLabel
  | SelectWithAriaLabelledBy
  | SelectWithHiddenLabel
  | SelectWithVisibleLabel

type BaseSelectElementProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  keyof SelectProps | "multiple" | "size"
>

export type NativeSelectElementProps = BaseSelectElementProps &
  SelectProps & {
    complex?: false
  }

export type ComplexSelectElementProps = BaseSelectElementProps &
  SelectProps & {
    complex: true
  }

export type SelectElementProps =
  | NativeSelectElementProps
  | ComplexSelectElementProps

function normalizeSelectValue(
  value:
    | NativeSelectElementProps["defaultValue"]
    | NativeSelectElementProps["value"]
    | ComplexSelectElementProps["defaultValue"]
    | ComplexSelectElementProps["value"]
) {
  if (value === undefined || value === null) return undefined
  if (Array.isArray(value)) return value[0]
  return String(value)
}

// ---------------------------------------------------------------------------
// Select (public)
// ---------------------------------------------------------------------------

export const Select = forwardRef<HTMLSelectElement, SelectElementProps>(
  function Select(props: SelectElementProps, ref) {
    const {
      "aria-label": userAriaLabel,
      "aria-labelledby": userAriaLabelledBy,
      className,
      complex,
      defaultValue,
      description,
      hideLabel,
      id,
      invalid,
      label,
      onChange,
      options,
      placeholder,
      required,
      size,
      value,
      ...restProps
    } = props

    const triggerRef = useRef<HTMLButtonElement>(null)
    const stableId = useId()
    const controlId = id || `tds-select-${stableId}`
    const labelId = `${controlId}-label`
    const resolvedDescription =
      description || (invalid ? "Please select an option" : undefined)
    const descriptionId = resolvedDescription
      ? `${controlId}-description`
      : undefined
    const computedClassName = classNames(
      "tds-select",
      {
        "tds-select--invalid": invalid,
        "tds-select--lg": size === "lg",
        "tds-select--required": required,
      },
      className
    )

    const normalizedDefaultValue = normalizeSelectValue(defaultValue)
    const normalizedValue = normalizeSelectValue(value)

    const computedDefaultValue =
      normalizedDefaultValue === undefined && normalizedValue === undefined
        ? ""
        : normalizedDefaultValue
    const effectivePlaceholder =
      normalizedDefaultValue !== undefined && normalizedDefaultValue !== ""
        ? null
        : placeholder || "Select an option"
    const showLabel = !!label && !hideLabel
    const computedAriaLabel = userAriaLabel ?? (hideLabel ? label : undefined)
    const computedAriaLabelledBy =
      userAriaLabelledBy ??
      (computedAriaLabel || !showLabel ? undefined : labelId)

    const sharedControlProps = {
      ...restProps,
      "aria-describedby": descriptionId,
      "aria-label": computedAriaLabel,
      "aria-labelledby": computedAriaLabelledBy,
      defaultValue: computedDefaultValue,
      invalid,
      options,
      placeholder: effectivePlaceholder,
      required,
      value: normalizedValue,
    }

    const handleComplexLabelClick = () => triggerRef.current?.focus()

    const SelectComponent = complex ? SelectPopover : SelectNative

    return (
      <div className={computedClassName}>
        {showLabel &&
          (complex ? (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- mirrors native label click-to-focus; keyboard users interact with the combobox directly
            <div
              className="tds-select-label"
              id={labelId}
              onClick={handleComplexLabelClick}
            >
              {label}
            </div>
          ) : (
            <label htmlFor={controlId} id={labelId}>
              {label}
            </label>
          ))}
        <SelectComponent
          {...sharedControlProps}
          ref={ref}
          id={controlId}
          onChange={onChange}
          {...(complex && { triggerRef })}
        />
        {resolvedDescription && descriptionId && (
          <p className="tds-select-description" id={descriptionId}>
            <Icon
              aria-hidden
              className="tds-select-description-invalid-icon"
              symbol="general#exclamation-triangle"
            />
            {resolvedDescription}
          </p>
        )}
      </div>
    )
  }
)

Select.displayName = "Select"
